import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';
import { flatMap, isObject, isString } from 'lodash-es';
import { tap } from 'rxjs/operators';

import {
  AprimaAnalyticsService,
  ConfigurationRepository,
} from 'webcommon/legacy-common';
import { ServerUtilRepository } from 'webcommon/webapi';
import {
  getAdditionalInfo,
  isAngularJsHttpResponse,
  isCommandResult,
  isMessageError,
  isModelStateError,
  logAllErrors, logInnerErrors
} from 'webcommon/shared';


const genericErrorMessage = 'An error has occurred and the process cannot be completed.';
const newLine = '\r\n';

// This service handles all unhandled errors in the application,
// and provides an API for sending error reports to the server.
@Injectable({
  providedIn: 'root',
})
export class ApErrorHandler extends ErrorHandler {
  static readonly ajsFactoryName = 'ApErrorHandler';

  constructor(
    private readonly aprimaAnalyticsService: AprimaAnalyticsService,
    private readonly configurationRepository: ConfigurationRepository,
    private readonly serverUtilRepository: ServerUtilRepository,
  ) {
    super();
  }

  // This is the unhandled exception handler.
  // This should never be called directly,
  // only by Angular or the ajs AprimaExceptionHandler.
  override handleError(error: any): void {
    try {
      super.handleError(error);
      // super.handlError logs the main error with console.error,
      // but this call will include inner errors
      logInnerErrors(error);


      if (isError(error)) {
        this.reportBackgroundError(error);
      }
    } catch (e: any) {
      this.handleErrorFromHandlingError(e);
    }
  }

  // This method reports the error to the server without showing any UI.
  // This probably should never be called directly because if the error is being caught,
  // it's probably important enough to be showing the error to the user.
  // If the error needs to be caught without showing UI,
  // the correct thing to do is to rethrow the error so that it gets caught by the unhandled exception handler above.
  reportBackgroundError(error: Error): void {
    try {
      this.reportError(error, false);
    } catch (e: any) {
      this.handleErrorFromHandlingError(e);
    }
  }

  // This method reports the error to the server without showing any UI,
  // but reports the error to the server as being visible to the user so the caller must have shown its own error message first.
  // This should be called by code that was able to show an error message with more context to the user than the standard one.
  reportUserVisibleError(error: Error): void {
    logAllErrors(error);
    try {
      this.reportError(error, true);
    } catch (e: any) {
      this.handleErrorFromHandlingError(e);
    }
  }

  // same as reportUserVisibleError, but handles unknown catch blocks, where the value isn't guaranteed
  // to be of type Error
  reportUserVisibleErrorUnknown(error: unknown): void {
    this.reportUserVisibleError(this.convertToError(error, true));
  }

  // return void instead of some async value, so that this method can be a fire and forget
  private reportError(error: Error, withUI: boolean): void {
    try {
      this.aprimaAnalyticsService.sendException(error.message);
    } catch (sendExc: unknown) {
      logAllErrors(sendExc);
    }

    if (this.configurationRepository.DISABLE_ERROR_REPORTING) {
      return;
    }

    this.serverUtilRepository.reportError({
      Message: error.message,
      StackTrace: error.stack,
      WithUI: withUI,
      AdditionalInfo: getAdditionalInfo(error),
    }, {
      disableUnAuthorizedRedirect: true,
    }).pipe(
      tap(dto => {
        // eslint-disable-next-line no-console
        console.debug('Reported error', dto);
      }, (e: unknown) => {
        this.handleErrorFromHandlingError(e);
      })
    ).subscribe();
  }

  private handleErrorFromHandlingError(error: unknown): void {
    logAllErrors(error);
  }

  // pass skipReporting: true in the case that the Error returned will get reported
  // in a separate call
  private convertToError(error: unknown, skipReporting: boolean): Error {
    if (isError(error)) {
      return error;
    }
    const msg = this.getErrors(error, skipReporting).join(newLine);
    return new Error(msg);
  }

  getErrors(error: unknown, skipReporting?: boolean): string[] {
    if (!error) {
      return [genericErrorMessage];
    }

    if (isString(error)) {
      return [error];
    }

    if (error instanceof Error) {
      if (!skipReporting) {
        this.reportUserVisibleError(error);
      }

      return [error.message];
    }

    if (error instanceof HttpErrorResponse && error.error) {
      const httpErrors = this.findErrors(error.error);
      if (httpErrors.length) {
        return httpErrors;
      }

      if (error.statusText) {
        return [
          'An error has occurred. Status: ' + error.status + ': ' + error.statusText,
        ];
      }
    }

    if (isAngularJsHttpResponse(error) && error.data) {
      const httpErrors = this.findErrors(error.data);
      if (httpErrors.length) {
        return httpErrors;
      }

      if (error.status && error.statusText) {
        return [
          'An error has occurred. Status: ' + error.status + ': ' + error.statusText,
        ];
      }
    }

    const errors = this.findErrors(error);
    if (errors.length) {
      return errors;
    }

    return [genericErrorMessage];
  }

  private findErrors(error: unknown): string[] {
    if (isModelStateError(error)) {
      const modelStateErrors = flatMap(error.ModelState, value => {
        return value || [];
      });
      if (modelStateErrors.length) {
        return modelStateErrors;
      }
    }

    if (isCommandResult(error) && error.ValidationResult && !error.ValidationResult.IsValid) {
      const validationResultErrors = error.ValidationResult.Errors.map(x => x.ErrorMessage);
      if (validationResultErrors.length) {
        return validationResultErrors;
      }
    }

    if (isMessageError(error) && error.Message) {
      return [error.Message];
    }

    return [];
  }
}

function isError(error: any): error is Error {
  return error && isObject(error) && 'message' in error && 'name' in error;
}
